/**
 * « Donner aussi à » d'autres classes du même niveau (chantier composition,
 * étape 6, 2026-09-02). Modules purs, sans React.
 *
 * Par défaut les classes supplémentaires reçoivent LES MÊMES questions (une
 * question de banque appartient à plusieurs séances : une création groupée,
 * partagée). Avec l'option « nombres différents pour chaque classe », les
 * exercices générés sont retirés au sort à nouveau pour chaque classe : même
 * carte, même format, nombres différents. Les questions écrites à la main
 * (elles ont un id) sont toujours partagées telles quelles.
 */

import { estDemiGroupe } from './groupesDeBesoin';

/** Niveau d'une classe = premier caractère de son nom (« 6A » → « 6 »). */
const niveauDe = (nom) => String(nom || '').charAt(0);

/** Niveau d'un groupe = celui de sa première classe (« 6A »), à défaut son nom. */
const niveauDuGroupe = (groupe) => niveauDe(groupe?.classes_detail?.[0] || groupe?.nom);

/**
 * Autres classes du même niveau que la classe courante, où le prof a un
 * accès complet. `estAccesComplet(id)` est injecté (utils/permissions).
 */
export const autresClassesDuNiveau = (classes, idCourant, nomCourant, estAccesComplet) => {
    const niveau = niveauDe(nomCourant);
    if (!niveau) return [];
    return (classes || [])
        .filter((c) => c && String(c.id) !== String(idCourant) && niveauDe(c.nom) === niveau && estAccesComplet(c.id))
        .map((c) => ({ id: c.id, nom: c.nom }))
        .sort((a, b) => String(a.nom).localeCompare(String(b.nom)));
};

/**
 * Autres groupes du prof, du même type et du même niveau que le groupe courant
 * (décision prof 2026-09-30) : d'un demi-groupe, ses autres demi-groupes ; d'un
 * groupe de besoin, ses autres groupes de besoin. Jamais de mélange : une
 * classe et son propre demi-groupe donneraient deux séances aux mêmes élèves.
 * Seuls les groupes du prof (`professeur.groupes`) : pas hasFullAccess, qui
 * confond les numéros de classes et ceux des groupes.
 */
export const autresGroupesDuNiveau = (groupes, professeur, groupeCourant) => {
    const niveau = niveauDuGroupe(groupeCourant);
    if (!groupeCourant || !niveau) return [];
    const demi = estDemiGroupe(groupeCourant);
    const duProf = new Set((professeur?.groupes || []).map(String));
    return (groupes || [])
        .filter((g) => g
            && String(g.id) !== String(groupeCourant.id)
            && duProf.has(String(g.id))
            && estDemiGroupe(g) === demi
            && niveauDuGroupe(g) === niveau)
        .map((g) => ({ id: g.id, nom: g.nom }))
        .sort((a, b) => String(a.nom).localeCompare(String(b.nom)));
};

/**
 * Nouvelle instance de chaque exercice généré (sans id) de la liste, même
 * attendu et même format ; les questions de banque (avec id) sont reprises
 * telles quelles. `_tmpId` et `_meta` sont conservés : le raccordement aux
 * ids de la création groupée reste identique (idParTmp).
 * Une génération qui échoue garde l'instance d'origine (jamais de trou).
 */
export const regenererGeneres = (questions, generateExercice) => (questions || []).map((q) => {
    if (!q || q.id != null) return q;
    const attenduId = q.attendu;
    const cle = q._meta?.generatorKey;
    if (!attenduId || !cle) return q;
    try {
        const gen = generateExercice(attenduId, cle);
        if (!gen) return q;
        return {
            ...gen,
            attendu: gen.attendu ?? attenduId,
            niveau: gen.niveau ?? q.niveau,
            matiere: gen.matiere ?? q.matiere,
            _tmpId: q._tmpId,
            _meta: { ...(gen._meta || {}), ...(q._meta || {}), generatorKey: cle },
        };
    } catch {
        return q;
    }
});
